/**
 * The chat composer's behavior, one implementation for both skins (client-chat-screen 7.2):
 * the admin's `ChatComposer` and the client's Secure room composer render their own markup
 * and CSS around it.
 *
 * - The draft lives in sessionStorage under `draftScope`: restored on mount and on a scope
 *   change, written as the person types. Sending hands the words to the screen, which keeps
 *   them with their idempotency key until the server answers, so the draft empties with the pill.
 * - A message is 1 to 4096 characters after trimming, as the server counts them; longer text
 *   stays in the pill (`tooLong`, the skin shows the one-line limit note) and is never sent.
 * - The pill grows with the text up to `MAX_LINES`, then scrolls inside.
 * - The send button is a plain tap (Gabriel 2026-10-05: no press-and-hold, which could leave
 *   the button stuck squashed after a slide-off): the send fires on the tap, the button gives
 *   one springy squash, and the plane flies off up and to the right. Nothing animates while the
 *   finger is down. Reduced motion gets the quiet variant 6: a 7px nudge and fade. At rest
 *   every transform is none.
 * - Attachment mode (chat-composer-attach-preview D3, D5; the admin's Telegram room only): with
 *   an invite or form request attached, the field is bound to the attachment's own note (the
 *   screen keeps it), which starts empty and holds at most 500 characters. The typed message is
 *   set aside, untouched in its draft, and is back once the attachment goes (Gabriel
 *   2026-10-05). Send is live once the choice's preview has loaded, with an empty note too. The
 *   send squashes the button and nothing flies: the bubble that lands is the automatic message.
 *   It awaits the screen's send; only while the same attachment in the same conversation is on
 *   screen does an acceptance clear it, so a late answer clears nothing. While it is in flight
 *   the note is read-only and Send is disabled. The field lies over a mirror that draws the note
 *   between the locked quotation marks (the field holds the note alone). Only the shaded note
 *   slot scrolls (Gabriel 2026-10-05): the rest of the message, the button and the X stay still,
 *   and the composer grows upward to the slot's cap, never past it.
 */
import {
  useCallback, useEffect, useLayoutEffect, useRef, useState, type ChangeEvent, type PointerEvent,
} from 'react';
import { animate, useMotionValue, useReducedMotion, type MotionValue } from 'framer-motion';
import { readDraft, REPLY_MAX_CHARS, writeDraft } from '@/lib/chat/chatDraft';
import type { SendOrigin } from './sendLanding';

/** Lines the pill grows to before it scrolls. */
const MAX_LINES = 5;
/** The invite and form request routes cap their message at this, as the server counts it. */
export const ATTACHMENT_NOTE_MAX_CHARS = 500;
/** The plane's flight on a tap. */
const FLIGHT_MS = 380;
const FLIGHT_EASE = [0.5, 0, 0.9, 0.4] as const;
const QUIET_EASE = [0.16, 1, 0.3, 1] as const;

const to = (value: MotionValue<number>, target: number, transition: object) => animate(value, target, transition);
/** Names an attachment send's fence: the conversation and the attachment. */
const fenceOf = (scope: string, key: string) => `${scope}\n${key}`;

/** The invite or form request in the composer (attachment mode, D5). */
export interface ComposerAttachment {
  /** Names this attachment; a replace or a remove changes it. */
  key: string;
  /** The current choice's preview has loaded: the attachment can be sent. */
  ready: boolean;
  /** The attachment's own note: the field's value while it is attached. */
  note: string;
  onNoteChange: (note: string) => void;
  /** Sends the attachment with the trimmed note; resolves once accepted, rejects when not. */
  send: (note: string) => Promise<void>;
  /** The send was accepted while this attachment was still on screen: the screen drops it. */
  onAccepted: () => void;
}

export interface ChatComposerBehaviorOptions {
  /** The sessionStorage scope of the draft (a client id, a client and room, or the client's own user). */
  draftScope: string;
  onSend: (text: string, origin: SendOrigin | null) => void;
  /** Called on every edit of the pill (the Secure room's typing signal). */
  onType?: () => void;
  /** An attached invite or form request: the field becomes its note (D5). */
  attachment?: ComposerAttachment | null;
}

export function useChatComposer({ draftScope, onSend, onType, attachment = null }: ChatComposerBehaviorOptions) {
  const [text, setText] = useState(() => readDraft(draftScope));
  const [flying, setFlying] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const pillRef = useRef<HTMLDivElement>(null);
  // Attachment mode: the note slot's scroller, the note's mirror, the mirror's note text and its
  // opening mark.
  const slotScrollRef = useRef<HTMLDivElement>(null);
  const mirrorRef = useRef<HTMLDivElement>(null);
  const noteRef = useRef<HTMLSpanElement>(null);
  const quoteRef = useRef<HTMLSpanElement>(null);
  const reduced = useReducedMotion() === true;
  const attached = attachment !== null;
  /** What the field shows: the attachment's note, or the draft (set aside, untouched, meanwhile). */
  const field = attachment ? attachment.note : text;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Another conversation: that conversation's draft.
  const scopeRef = useRef(draftScope);
  useEffect(() => {
    if (scopeRef.current === draftScope) return;
    scopeRef.current = draftScope;
    setText(readDraft(draftScope));
  }, [draftScope]);

  // Grow and shrink with the text up to MAX_LINES, then scroll inside the pill. Measuring must never
  // change the composer's size mid-way: collapsing the field to measure let the thread grow for one
  // layout pass, the browser clamped the thread's scroll down a line, and the newest bubble ended up
  // under the composer (phone 2026-10-03, on typing and again on backspace). So while the field is
  // collapsed to measure, the pill is held at its current height; the composer changes size once,
  // to the final height, which the thread compensates in one step.
  useLayoutEffect(() => {
    if (attached) return;
    const input = inputRef.current;
    const pill = pillRef.current;
    if (!input || !pill) return;
    const held = pill.getBoundingClientRect().height;
    if (held > 0) {
      pill.style.height = `${held}px`;
      pill.style.overflow = 'hidden';
    }
    input.style.height = 'auto';
    const line = Number.parseFloat(getComputedStyle(input).lineHeight) || 22;
    input.style.height = `${Math.min(input.scrollHeight, line * MAX_LINES)}px`;
    pill.style.height = '';
    pill.style.overflow = '';
  }, [attached, text]);

  // Attachment mode: the field lies over the mirror at its size and never scrolls itself; the two
  // scroll together inside the slot, which the stylesheet caps. The field's first line starts after
  // the opening mark (text-indent at the mark's width), so the caret lives between the marks.
  const measureAttachment = useCallback(() => {
    const input = inputRef.current;
    if (!input) return;
    // Both marks are the same glyph: the opening one indents the field's first line, and the
    // closing one hangs into right padding of the same width in the field and the mirror alike,
    // so the two wrap at exactly the same words.
    const quoteWidth = quoteRef.current ? quoteRef.current.getBoundingClientRect().width : 0;
    input.style.textIndent = `${quoteWidth}px`;
    input.parentElement?.style.setProperty('--note-quote-w', `${quoteWidth}px`);
  }, []);

  useLayoutEffect(() => {
    if (!attached) return;
    measureAttachment();
  }, [attached, field, measureAttachment]);

  // Caret follow after an edit: at the end, the slot shows its bottom; elsewhere, the caret's line
  // is scrolled into view in the slot, measured on the mirror.
  const followCaretRef = useRef(false);
  useLayoutEffect(() => {
    if (!followCaretRef.current) return;
    followCaretRef.current = false;
    const input = inputRef.current;
    const block = slotScrollRef.current;
    if (!attached || !input || !block) return;
    const caret = input.selectionEnd ?? field.length;
    if (caret >= field.length) {
      block.scrollTop = block.scrollHeight;
      return;
    }
    const node = noteRef.current?.firstChild;
    if (!node) return;
    const range = document.createRange();
    if (typeof range.getBoundingClientRect !== 'function') return;
    range.setStart(node, Math.min(caret, node.textContent?.length ?? 0));
    range.collapse(true);
    const at = range.getBoundingClientRect();
    const view = block.getBoundingClientRect();
    if (at.bottom > view.bottom) block.scrollTop += at.bottom - view.bottom;
    else if (at.top < view.top) block.scrollTop -= view.top - at.top;
  }, [attached, field]);

  const onTypeRef = useRef(onType);
  onTypeRef.current = onType;
  const change = (event: ChangeEvent<HTMLTextAreaElement>) => {
    followCaretRef.current = true;
    if (attachment) {
      attachment.onNoteChange(event.target.value);
      return;
    }
    setText(event.target.value);
    writeDraft(draftScope, event.target.value);
    if (event.target.value.trim().length > 0) onTypeRef.current?.();
  };

  // The button (x, scaleX, scaleY) and the plane inside it (x, y, rotate, scale, opacity).
  const bx = useMotionValue(0);
  const bsx = useMotionValue(1);
  const bsy = useMotionValue(1);
  const gx = useMotionValue(0);
  const gy = useMotionValue(0);
  const gr = useMotionValue(0);
  const gs = useMotionValue(1);
  const go = useMotionValue(1);

  const focusedRef = useRef(false);
  const flightRef = useRef(0);
  const flyingTimerRef = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(flyingTimerRef.current), []);

  // An attachment send in flight, named by its conversation and attachment: it fences only that
  // attachment, so a replace or another room is free at once.
  const [pendingFor, setPendingFor] = useState<string | null>(null);
  const attachmentRef = useRef(attachment);
  attachmentRef.current = attachment;
  const currentFence = attachment ? fenceOf(draftScope, attachment.key) : null;
  const pending = currentFence !== null && pendingFor === currentFence;
  const currentFenceRef = useRef(currentFence);
  currentFenceRef.current = currentFence;

  const words = field.trim();
  const tooLong = words.length > (attachment ? ATTACHMENT_NOTE_MAX_CHARS : REPLY_MAX_CHARS);
  const live = attachment
    ? attachment.ready && !tooLong && !pending
    : words.length > 0 && !tooLong;

  /** The tap's one springy squash: from a squeezed shape back to rest. */
  const tapPulse = () => {
    bx.jump(-3); bsx.jump(0.9); bsy.jump(0.94);
    const spring = { type: 'spring', stiffness: 700, damping: 18 };
    to(bx, 0, spring); to(bsx, 1, spring); to(bsy, 1, spring);
  };

  const fly = () => {
    const flight = ++flightRef.current;
    if (reduced) {
      // Variant 6: nudge 7px up and right while fading, then fade back in place.
      const out = { duration: 0.153, ease: QUIET_EASE };
      void Promise.all([to(gx, 7, out), to(gy, -7, out), to(go, 0, out)]).then(() => {
        if (flight !== flightRef.current) return;
        gx.jump(0); gy.jump(0); gs.jump(0.9);
        const back = { duration: 0.187, ease: QUIET_EASE };
        to(gs, 1, back); to(go, 1, back);
      });
      return 153;
    }
    tapPulse();
    const flightMs = FLIGHT_MS;
    const out = { duration: flightMs / 1000, ease: FLIGHT_EASE };
    void Promise.all([to(gx, 52, out), to(gy, -128, out), to(gr, -22, out), to(gs, 0.82, out), to(go, 0.3, out)]).then(() => {
      if (flight !== flightRef.current) return;
      // A fresh plane springs back into the button.
      gx.jump(-8); gy.jump(8); gr.jump(0); gs.jump(0.3); go.jump(0);
      const back = { type: 'spring', stiffness: 520, damping: 24 };
      to(gx, 0, back); to(gy, 0, back); to(gs, 1, back); to(go, 1, back);
    });
    return flightMs;
  };

  // Remembers whether the keyboard was up when the finger came down, so the send can keep it up.
  const onPointerDown = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    focusedRef.current = document.activeElement === inputRef.current;
  };

  const sendAttachment = (current: ComposerAttachment) => {
    if (!reduced) tapPulse();
    const fence = fenceOf(draftScope, current.key);
    setPendingFor(fence);
    current.send(words).then(
      () => {
        // Only the attachment that was sent, still on screen in the same conversation, goes.
        if (!mountedRef.current || currentFenceRef.current !== fence) return;
        attachmentRef.current?.onAccepted();
      },
      () => undefined, // refused or failed: the attachment and the note stay (the screen says why)
    ).finally(() => {
      if (mountedRef.current) setPendingFor((now) => (now === fence ? null : now));
    });
    if (focusedRef.current) inputRef.current?.focus({ preventScroll: true });
    focusedRef.current = false;
  };

  const send = () => {
    if (!live) return;
    if (attachment) {
      sendAttachment(attachment);
      return;
    }
    const input = inputRef.current;
    let origin: SendOrigin | null = null;
    if (input) {
      const style = getComputedStyle(input);
      origin = { rect: input.getBoundingClientRect(), fontSize: Number.parseFloat(style.fontSize) || 16, lineHeight: style.lineHeight };
    }
    const flightMs = fly();
    // The button stays filled while the plane is in the air, then goes quiet with the empty pill.
    setFlying(true);
    clearTimeout(flyingTimerRef.current);
    flyingTimerRef.current = setTimeout(() => setFlying(false), flightMs);
    setText('');
    writeDraft(draftScope, ''); // the words now wait under their key until the server answers
    onSend(words, origin);
    if (focusedRef.current) input?.focus({ preventScroll: true }); // keep the keyboard up between messages
    focusedRef.current = false;
  };

  return {
    /** The field's value: the attachment's note while one is attached, else the draft. */
    text: field,
    change,
    inputRef,
    pillRef,
    /** True while the text can be sent: the button is filled. */
    live,
    /** True while the plane is in the air: the button stays filled. */
    flying,
    tooLong,
    /** An attachment send is in flight: the note is read-only and Send is disabled. */
    pending,
    reduced,
    slotScrollRef,
    mirrorRef,
    noteRef,
    quoteRef,
    /** Spread on the send button (a `motion.button`). */
    sendButton: {
      style: { x: bx, scaleX: bsx, scaleY: bsy },
      disabled: !live,
      onPointerDown,
      // Keep focus (and the keyboard) in the pill when the button is pressed.
      onMouseDown: (event: { preventDefault: () => void }) => event.preventDefault(),
      onClick: send,
    },
    /** The plane's motion style (a `motion.svg` inside the button). */
    glyphStyle: { x: gx, y: gy, rotate: gr, scale: gs, opacity: go },
    focusPill: () => inputRef.current?.focus({ preventScroll: true }),
  };
}

/** The solid notched paper plane, angled up-right (Gabriel 2026-10-03). */
export const SEND_GLYPH_PATH = 'M3.4 3.2c-.8-.4-1.7.4-1.4 1.3L4.4 11l9.6 1-9.6 1-2.4 6.5c-.3.9.6 1.7 1.4 1.3l18-8.1c.8-.4.8-1.5 0-1.9z';
